import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface NewsSource {
  id: string
  name: string
  url: string
  status: 'active' | 'inactive'
  frequency_minutes: number
  source_type?: 'rss' | 'scraping'
  scrape_url?: string | null
  last_fetched_at: string | null
  error_count?: number
  last_error?: string | null
  created_at: string
}

export interface RssFeed {
  id: string
  title: string
  subtitle: string | null
  content: string | null
  summary: string | null
  image_url: string | null
  source_id: string | null
  source_name: string | null
  original_url: string | null
  language: string
  status: 'draft' | 'review' | 'published' | 'archived'
  category: string | null
  tags: string[]
  author: string | null
  published_date: string | null
  fetched_at: string
  processed_at: string | null
  created_at: string
}

export interface ClipLog {
  id: string
  source_id: string | null
  action: string
  details: Record<string, unknown> | null
  items_count: number
  created_at: string
}

export interface ClipStats {
  totalToday: number
  totalWeek: number
  pending: number
  published: number
  archived: number
  sourcesActive: number
  sourcesInactive: number
  totalSources: number
}

export function useClipSources() {
  const [sources, setSources] = useState<NewsSource[]>([])
  const [loading, setLoading] = useState(true)

  const fetchSources = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('news_sources')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) console.error('Error fetching sources:', error)
    setSources(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchSources()
  }, [fetchSources])

  async function createSource(source: Partial<NewsSource>) {
    const { data, error } = await supabase.from('news_sources').insert(source).select().single()
    if (error) {
      console.error('Error creating source:', error)
      return null
    }
    return data
  }

  async function updateSource(id: string, source: Partial<NewsSource>) {
    const { data, error } = await supabase.from('news_sources').update(source).eq('id', id).select().single()
    if (error) {
      console.error('Error updating source:', error)
      return null
    }
    return data
  }

  async function deleteSource(id: string) {
    const { error } = await supabase.from('news_sources').delete().eq('id', id)
    if (error) {
      console.error('Error deleting source:', error)
      return false
    }
    return true
  }

  async function toggleSourceStatus(id: string, currentStatus: string) {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    return updateSource(id, { status: newStatus })
  }

  async function fetchRSSNow(id: string) {
    const { data, error } = await supabase
      .from('news_sources')
      .update({ last_fetched_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (error) {
      console.error('Error updating fetch time:', error)
      return null
    }
    return data
  }

  return { sources, loading, fetchSources, createSource, updateSource, deleteSource, toggleSourceStatus, fetchRSSNow }
}

export function useClipFeeds() {
  const [feeds, setFeeds] = useState<RssFeed[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const fetchFeeds = useCallback(async (
    page = 1,
    perPage = 10,
    search = '',
    status = '',
    sourceId = '',
    startDate = '',
    endDate = ''
  ) => {
    setLoading(true)
    let query = supabase.from('rss_feeds').select('*', { count: 'exact' }).order('fetched_at', { ascending: false })
    if (search) query = query.ilike('title', `%${search}%`)
    if (status) query = query.eq('status', status)
    if (sourceId) query = query.eq('source_id', sourceId)
    if (startDate) query = query.gte('published_date', `${startDate}T00:00:00`)
    if (endDate) query = query.lte('published_date', `${endDate}T23:59:59`)
    const from = (page - 1) * perPage
    query = query.range(from, from + perPage - 1)
    const { data, error, count } = await query
    if (error) console.error('Error fetching feeds:', error)
    setFeeds(data || [])
    setTotalCount(count || 0)
    setLoading(false)
  }, [])

  async function fetchFeedIds(
    search = '',
    status = '',
    sourceId = '',
    startDate = '',
    endDate = ''
  ) {
    let query = supabase.from('rss_feeds').select('id,status').order('fetched_at', { ascending: false })
    if (search) query = query.ilike('title', `%${search}%`)
    if (status) query = query.eq('status', status)
    if (sourceId) query = query.eq('source_id', sourceId)
    if (startDate) query = query.gte('published_date', `${startDate}T00:00:00`)
    if (endDate) query = query.lte('published_date', `${endDate}T23:59:59`)
    const { data, error } = await query
    if (error) {
      console.error('Error fetching feed ids:', error)
      return []
    }
    return data || []
  }

  async function updateFeedStatus(id: string, status: string) {
    const updates: Record<string, unknown> = { status }
    if (status === 'published') {
      updates.processed_at = new Date().toISOString()
    }
    const { data, error } = await supabase.from('rss_feeds').update(updates).eq('id', id).select().single()
    if (error) {
      console.error('Error updating feed:', error)
      return null
    }
    return data
  }

  async function deleteFeed(id: string) {
    const { error } = await supabase.from('rss_feeds').delete().eq('id', id)
    if (error) {
      console.error('Error deleting feed:', error)
      return false
    }
    return true
  }

  async function updateFeed(id: string, feed: Partial<RssFeed>) {
    const { data, error } = await supabase.from('rss_feeds').update(feed).eq('id', id).select().single()
    if (error) {
      console.error('Error updating feed:', error)
      return null
    }
    return data
  }

  async function publishToNews(feed: RssFeed, options?: { is_featured?: boolean; is_headline?: boolean; is_breaking?: boolean }) {
    const sourceName = feed.source_name?.trim() || ''
    const sourceUrl = feed.original_url?.trim() || null
    const slug = feed.title
      ?.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'noticia'

    const { data, error } = await supabase
      .from('news')
      .insert({
        title: feed.title,
        subtitle: feed.subtitle || '',
        content: feed.content || feed.summary || '',
        image_url: feed.image_url,
        category: feed.category || 'Geral',
        tags: feed.tags || [],
        author: feed.author || sourceName || 'Central Alvinegra',
        source: sourceName,
        source_url: sourceUrl,
        meta_description: feed.summary?.substring(0, 160) || '',
        slug: slug,
        status: 'publicado',
        is_featured: options?.is_featured || false,
        is_headline: options?.is_headline || false,
        is_breaking: options?.is_breaking || false,
        published_at: new Date().toISOString(),
        views: 0,
      })
      .select()
      .single()

    if (error) {
      console.error('Error publishing to news:', error)
      return null
    }

    // Mark feed as published
    await supabase.from('rss_feeds').update({ status: 'published', processed_at: new Date().toISOString() }).eq('id', feed.id)

    return data
  }

  return { feeds, loading, totalCount, fetchFeeds, fetchFeedIds, updateFeedStatus, deleteFeed, updateFeed, publishToNews }
}

export function useClipStats() {
  const [stats, setStats] = useState<ClipStats>({
    totalToday: 0,
    totalWeek: 0,
    pending: 0,
    published: 0,
    archived: 0,
    sourcesActive: 0,
    sourcesInactive: 0,
    totalSources: 0,
  })
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    const today = new Date().toISOString().split('T')[0]
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()

    const [todayRes, weekRes, pendingRes, publishedRes, archivedRes, sourcesRes] = await Promise.all([
      supabase.from('rss_feeds').select('id', { count: 'exact' }).gte('fetched_at', today),
      supabase.from('rss_feeds').select('id', { count: 'exact' }).gte('fetched_at', weekAgo),
      supabase.from('rss_feeds').select('id', { count: 'exact' }).eq('status', 'draft'),
      supabase.from('rss_feeds').select('id', { count: 'exact' }).eq('status', 'published'),
      supabase.from('rss_feeds').select('id', { count: 'exact' }).eq('status', 'archived'),
      supabase.from('news_sources').select('*'),
    ])

    const sources = sourcesRes.data || []
    setStats({
      totalToday: todayRes.count || 0,
      totalWeek: weekRes.count || 0,
      pending: pendingRes.count || 0,
      published: publishedRes.count || 0,
      archived: archivedRes.count || 0,
      sourcesActive: sources.filter((s) => s.status === 'active').length,
      sourcesInactive: sources.filter((s) => s.status === 'inactive').length,
      totalSources: sources.length,
    })
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return { stats, loading, refetch: fetchStats }
}

export function useClipLog() {
  const [logs, setLogs] = useState<ClipLog[]>([])
  const [loading, setLoading] = useState(true)

  const fetchLogs = useCallback(async (limit = 50) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('clip_log')
      .select('*, news_sources(name)')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) console.error('Error fetching logs:', error)
    setLogs(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  return { logs, loading, refetch: fetchLogs }
}
